/**
 * 🛸 STATION LPE - BASE PRINCIPALE
 * Footer spatial avec coordonnées de la station
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Clock, Globe, ExternalLink, Rocket } from 'lucide-react';
import { CGUModal, PrivacyModal, MentionsLegalesModal } from './LegalModals';

const FooterSection = ({ isDarkMode }) => {
  // États pour les modales légales
  const [showCGU, setShowCGU] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showMentions, setShowMentions] = useState(false);
  return (
    <motion.div 
      className="max-w-4xl mx-auto mt-10 mb-8"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.7 }}
    >
      <div
        className="relative overflow-hidden rounded-2xl"
        style={{
          background: 'linear-gradient(135deg, #020617 0%, #0f172a 50%, #1e1b4b 100%)',
          boxShadow: '0 0 60px rgba(99, 102, 241, 0.25), 0 25px 50px rgba(0,0,0,0.5)',
          border: '1px solid rgba(99, 102, 241, 0.3)'
        }}
      >
        {/* Étoiles animées */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(30)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-1 h-1 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                background: ['#6366f1', '#8b5cf6', '#ec4899', '#06b6d4'][i % 4],
                opacity: Math.random() * 0.4 + 0.1
              }}
              animate={{ opacity: [0.1, 0.6, 0.1], scale: [1, 1.5, 1] }}
              transition={{ duration: 2 + Math.random() * 3, repeat: Infinity, delay: Math.random() * 2 }}
            />
          ))}
        </div>

        {/* Animated gradient border */}
        <motion.div
          className="absolute inset-0 rounded-2xl pointer-events-none"
          style={{
            background: 'linear-gradient(90deg, #6366f1, #ec4899, #06b6d4, #22c55e, #6366f1)',
            backgroundSize: '300% 100%',
            padding: '2px',
            mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
            maskComposite: 'xor',
            WebkitMaskComposite: 'xor'
          }}
          animate={{ backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'] }}
          transition={{ duration: 5, repeat: Infinity, ease: 'linear' }}
        />

        {/* HUD Corners */}
        <div className="absolute top-3 left-3 w-5 h-5 border-t-2 border-l-2 border-indigo-500" />
        <div className="absolute top-3 right-3 w-5 h-5 border-t-2 border-r-2 border-pink-500" />
        <div className="absolute bottom-3 left-3 w-5 h-5 border-b-2 border-l-2 border-indigo-500/50" />
        <div className="absolute bottom-3 right-3 w-5 h-5 border-b-2 border-r-2 border-pink-500/50" />

        {/* Header */}
        <div className="relative px-6 py-3 border-b border-indigo-500/20">
          <div className="flex items-center justify-center gap-3">
            <motion.div
              className="w-2 h-2 rounded-full bg-green-500"
              animate={{ opacity: [1, 0.5, 1], scale: [1, 1.2, 1] }}
              transition={{ duration: 1, repeat: Infinity }}
            />
            <span className="text-[10px] font-mono text-indigo-400 tracking-[0.3em]">STATION LPE • BASE PRINCIPALE</span>
            <motion.div
              className="w-2 h-2 rounded-full bg-green-500"
              animate={{ opacity: [1, 0.5, 1], scale: [1, 1.2, 1] }}
              transition={{ duration: 1, repeat: Infinity }}
            />
          </div>
        </div>

        {/* Content */}
        <div className="relative p-8 text-center">
          {/* Floating particles */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {[...Array(8)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-2 h-2 rounded-full"
                style={{
                  background: 'rgba(99, 102, 241, 0.5)',
                  left: `${10 + i * 12}%`,
                  bottom: '10%',
                }}
                animate={{ y: [0, -150], opacity: [0, 0.8, 0], scale: [0.5, 1, 0.5] }}
                transition={{ duration: 4, delay: i * 0.5, repeat: Infinity }}
              />
            ))}
          </div>

          {/* Logo animé */}
          <motion.div
            className="inline-block mb-6 relative"
            animate={{ y: [0, -5, 0] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
          >
            <motion.div
              className="absolute inset-0 rounded-full blur-xl opacity-50"
              style={{ background: 'linear-gradient(135deg, #6366f1, #ec4899)' }}
              animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.6, 0.3] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
            <div 
              className="relative w-20 h-20 rounded-2xl flex items-center justify-center"
              style={{
                background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(236, 72, 153, 0.2) 100%)',
                border: '2px solid rgba(99, 102, 241, 0.5)',
                boxShadow: '0 0 30px rgba(99, 102, 241, 0.3)'
              }}
            >
              <motion.span 
                className="text-4xl"
                animate={{ rotate: [0, 5, -5, 0], scale: [1, 1.1, 1] }}
                transition={{ duration: 4, repeat: Infinity }}
              >
                🛒
              </motion.span>
            </div>
          </motion.div>
          
          {/* Titre */}
          <motion.h3 
            className="text-3xl sm:text-4xl font-black text-white mb-2"
            style={{ textShadow: '0 0 30px rgba(99, 102, 241, 0.5)' }}
          >
            La Petite Épicerie
          </motion.h3>
          
          <motion.p 
            className="font-bold text-xl mb-6"
            style={{
              background: 'linear-gradient(90deg, #06b6d4, #ec4899, #6366f1)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
            animate={{ opacity: [0.8, 1, 0.8] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            ✨ L'épicerie qui te chouchoute ✨
          </motion.p>

          {/* Separator */}
          <motion.div 
            className="w-40 h-0.5 mx-auto mb-6 rounded-full"
            style={{ background: 'linear-gradient(90deg, transparent, rgba(99, 102, 241, 0.5), transparent)' }}
            animate={{ scaleX: [0.5, 1, 0.5], opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 2, repeat: Infinity }}
          />

          {/* Coordonnées style spatial */}
          <div className="space-y-3 max-w-md mx-auto">
            {/* Localisation */}
            <motion.div 
              className="flex items-center justify-center gap-3 p-3 rounded-xl"
              style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(99, 102, 241, 0.2)' }}
              whileHover={{ scale: 1.02, borderColor: 'rgba(99, 102, 241, 0.5)' }}
            >
              <MapPin className="w-5 h-5 text-indigo-400" />
              <div className="text-left">
                <p className="text-[10px] text-gray-500 font-mono">COORDONNÉES TERRESTRES</p>
                <p className="text-white font-medium">138 Rue Arthur Lamendin, 62980 Vermelles</p>
              </div>
            </motion.div>

            {/* Téléphone */}
            <motion.a 
              href="tel:0780648976"
              className="flex items-center justify-center gap-3 p-3 rounded-xl block"
              style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(6, 182, 212, 0.2)' }}
              whileHover={{ scale: 1.02, borderColor: 'rgba(6, 182, 212, 0.5)' }}
            >
              <Phone className="w-5 h-5 text-cyan-400" />
              <div className="text-left">
                <p className="text-[10px] text-gray-500 font-mono">CANAL DE COMMUNICATION</p>
                <p className="text-cyan-400 font-bold text-lg">07 80 64 89 76</p>
              </div>
            </motion.a>

            {/* Horaires */}
            <motion.div 
              className="flex items-center justify-center gap-3 p-3 rounded-xl"
              style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(34, 197, 94, 0.2)' }}
              whileHover={{ scale: 1.02, borderColor: 'rgba(34, 197, 94, 0.5)' }}
            >
              <Clock className="w-5 h-5 text-green-400" />
              <div className="text-left">
                <p className="text-[10px] text-gray-500 font-mono">HEURES D'OPÉRATION</p>
                <p className="text-white">7j/7 de 12h à minuit • <span className="text-green-400">Ven-Sam jusqu'à 2h</span></p>
              </div>
            </motion.div>
          </div>

          {/* Website button */}
          <motion.a
            href="https://www.la-petite-epicerie.shop"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 mt-8 px-8 py-4 rounded-xl font-bold text-white"
            style={{
              background: 'linear-gradient(135deg, #6366f1 0%, #ec4899 50%, #06b6d4 100%)',
              backgroundSize: '200% 200%',
              boxShadow: '0 0 30px rgba(99, 102, 241, 0.4)'
            }}
            animate={{ backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'] }}
            transition={{ duration: 4, repeat: Infinity }}
            whileHover={{ scale: 1.05, boxShadow: '0 0 50px rgba(99, 102, 241, 0.6)' }}
            whileTap={{ scale: 0.95 }}
          >
            <Globe className="w-5 h-5" />
            <span>PORTAIL OFFICIEL</span>
            <ExternalLink className="w-4 h-4" />
          </motion.a>
          
          {/* Legal links - Ouvrent en modale */}
          <div className="mt-6 flex items-center justify-center gap-4 text-sm">
            <button 
              onClick={() => setShowCGU(true)} 
              className="text-gray-500 hover:text-indigo-400 transition-colors font-mono text-xs cursor-pointer"
              data-testid="footer-cgu-link"
            >
              CGU
            </button>
            <span className="text-gray-700">•</span>
            <button 
              onClick={() => setShowPrivacy(true)} 
              className="text-gray-500 hover:text-indigo-400 transition-colors font-mono text-xs cursor-pointer"
              data-testid="footer-privacy-link"
            >
              CONFIDENTIALITÉ
            </button>
            <span className="text-gray-700">•</span>
            <button 
              onClick={() => setShowMentions(true)} 
              className="text-gray-500 hover:text-indigo-400 transition-colors font-mono text-xs cursor-pointer"
              data-testid="footer-mentions-link"
            >
              MENTIONS LÉGALES
            </button>
          </div>
        </div>

        {/* Modales légales */}
        <CGUModal isOpen={showCGU} onClose={() => setShowCGU(false)} isDarkMode={true} />
        <PrivacyModal isOpen={showPrivacy} onClose={() => setShowPrivacy(false)} isDarkMode={true} />
        <MentionsLegalesModal isOpen={showMentions} onClose={() => setShowMentions(false)} isDarkMode={true} />

        {/* Footer HUD */}
        <div className="px-6 py-3 border-t border-indigo-500/10 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Rocket className="w-4 h-4 text-indigo-500/50" />
            <span className="text-[10px] font-mono text-gray-600">STATION LPE v2.0</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-mono text-gray-600">SYSTÈME OPÉRATIONNEL</span>
            <div className="flex items-center gap-1">
              <motion.div className="w-1.5 h-1.5 rounded-full bg-indigo-500" animate={{ opacity: [1, 0.5, 1] }} transition={{ duration: 1, repeat: Infinity }} />
              <motion.div className="w-1.5 h-1.5 rounded-full bg-pink-500" animate={{ opacity: [1, 0.5, 1] }} transition={{ duration: 1, repeat: Infinity, delay: 0.2 }} />
              <motion.div className="w-1.5 h-1.5 rounded-full bg-cyan-500" animate={{ opacity: [1, 0.5, 1] }} transition={{ duration: 1, repeat: Infinity, delay: 0.4 }} />
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default FooterSection;
